// Client hooks for Milestone D collab features.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { autolinkHtml } from './utils';

export interface WorkspaceUser {
  id: number;
  email: string;
  name: string;
  picture: string | null;
  role: 'owner' | 'admin' | 'member';
}

export function useWorkspaceUsers() {
  return useQuery<{ users: WorkspaceUser[] }>({
    queryKey: ['workspace-users'],
    queryFn: async () => {
      const r = await fetch('/api/workspace/users', { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    staleTime: 60_000,
  });
}

// --- Comments -------------------------------------------------------------

export interface ThreadComment {
  id: number;
  authorUserId: number;
  authorName: string;
  authorEmail: string;
  authorPicture: string | null;
  body: string;
  createdAt: string;
}

export function useThreadComments(threadId: number | null) {
  return useQuery<{ comments: ThreadComment[] }>({
    queryKey: ['thread-comments', threadId],
    enabled: threadId != null,
    queryFn: async () => {
      const r = await fetch(`/api/threads/${threadId}/comments`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    refetchInterval: 15_000,
  });
}

export function useAddComment(threadId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: string) => {
      const r = await fetch(`/api/threads/${threadId}/comments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ body }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['thread-comments', threadId] }),
  });
}

export function useDeleteComment(threadId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (commentId: number) => {
      const r = await fetch(`/api/threads/${threadId}/comments/${commentId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['thread-comments', threadId] }),
  });
}

// --- Reply / send ---------------------------------------------------------

export interface SendQueuedResponse {
  ok: true;
  outgoingId: number;
  /** ISO timestamp — when the worker will actually fire the Gmail send. */
  sendAt: string;
}

export function useSendReply(threadId: number) {
  return useMutation<
    SendQueuedResponse,
    Error,
    {
      body: string;
      bodyHtml?: string;
      replyAll?: boolean;
      /** Explicit recipients from the composer (override server derivation). */
      to?: string[];
      cc?: string[];
      bcc?: string[];
      archiveAfterSend?: boolean;
      scheduledAt?: string;
      attachmentIds?: number[];
      signatureId?: number | null;
      fromAccountId?: number | null;
      lastSeenMessageId?: number;
      force?: boolean;
    }
  >({
    mutationFn: async ({
      body,
      bodyHtml,
      replyAll,
      to,
      cc,
      bcc,
      archiveAfterSend,
      scheduledAt,
      attachmentIds,
      signatureId,
      fromAccountId,
      lastSeenMessageId,
      force,
    }) => {
      const r = await fetch(`/api/threads/${threadId}/reply`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          body,
          bodyHtml: bodyHtml ? autolinkHtml(bodyHtml) : bodyHtml,
          replyAll: !!replyAll,
          to,
          cc,
          bcc,
          archiveAfterSend: archiveAfterSend ?? true,
          scheduledAt,
          attachmentIds,
          signatureId,
          fromAccountId,
          lastSeenMessageId,
          force,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        if (r.status === 409 && j.error === 'thread_changed') {
          // Collision guard tripped — carry the details so the composer can
          // show a meaningful confirm instead of a generic failure.
          throw Object.assign(new Error('thread_changed'), {
            threadChanged: {
              newMessage: j.newMessage ?? null,
              teammateReply: j.teammateReply ?? null,
            } satisfies ThreadChangedInfo,
          });
        }
        throw new Error(j.detail || j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
    // We don't invalidate here — the inbox-level wrapper handles the
    // optimistic archive + undo-toast flow.
  });
}

export interface PendingSend {
  id: number;
  accountId: number;
  kind: 'reply' | 'compose';
  threadId: number | null;
  toAddresses: string[];
  subject: string;
  sendAt: string;
  status: string;
  archiveAfterSend: boolean;
  createdAt: string;
}

/** Queued/sending outgoing messages for the current user — drives the
 *  'Sending…' rows shown at the top of the Sent folder. Polls briefly so the
 *  row clears as soon as the send actually leaves. */
export function useOutgoing() {
  return useQuery<{ outgoing: PendingSend[] }>({
    queryKey: ['outgoing'],
    queryFn: async () => {
      const r = await fetch('/api/outgoing', { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    refetchInterval: (q) => ((q.state.data?.outgoing.length ?? 0) > 0 ? 3_000 : 15_000),
    refetchOnWindowFocus: true,
  });
}

/** Cancel a queued outgoing message (the undo button). */
export function useCancelOutgoing() {
  return useMutation<{ ok: true }, Error, number>({
    mutationFn: async (outgoingId) => {
      const r = await fetch(`/api/outgoing/${outgoingId}/cancel`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ restoreDraft: true }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
  });
}

/** One audit-log entry for a thread's activity feed. */
export interface ActivityEntry {
  id: number;
  action: string;
  actor: string;
  meta: Record<string, unknown>;
  createdAt: string;
}

export function useThreadActivity(threadId: number | null) {
  return useQuery<{ activity: ActivityEntry[] }>({
    queryKey: ['thread-activity', threadId],
    enabled: threadId != null,
    queryFn: async () => {
      const r = await fetch(`/api/threads/${threadId}/activity`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
  });
}

/** Full content of a single queued outgoing message — backs the scheduled-send
 *  preview panel (subject, body, recipients, scheduled time). */
export interface OutgoingDetail {
  id: number;
  accountId: number;
  kind: 'reply' | 'compose';
  threadId: number | null;
  toAddresses: string[];
  ccAddresses: string[];
  bccAddresses: string[];
  subject: string;
  body: string;
  bodyHtml: string | null;
  sendAt: string;
  status: string;
  createdAt: string;
}

export function useOutgoingDetail(outgoingId: number | null) {
  return useQuery<OutgoingDetail>({
    queryKey: ['outgoing-detail', outgoingId],
    enabled: outgoingId != null,
    queryFn: async () => {
      const r = await fetch(`/api/outgoing/${outgoingId}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
  });
}

/** Move a still-queued send to a new time (also powers "Send now"). */
export function useRescheduleOutgoing() {
  return useMutation<{ ok: true; sendAt: string }, Error, { id: number; scheduledAt: string }>({
    mutationFn: async ({ id, scheduledAt }) => {
      const r = await fetch(`/api/outgoing/${id}/reschedule`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ scheduledAt }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
  });
}

// --- Drafts ---------------------------------------------------------------

export interface DraftPeer {
  authorUserId: number;
  authorName: string;
  authorEmail: string;
  updatedAt: string;
}

export interface DraftAttachment {
  id: number;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

export interface ThreadDraft {
  body: string;
  /** Saved-but-unsent attachment chips (still valid server-side). */
  attachments?: DraftAttachment[];
  updatedAt: string | null;
  peers: DraftPeer[];
}

export interface PresenceViewer {
  userId: number;
  name: string;
}

/** Heartbeat "I have this thread open" + learn who else does. One POST powers
 *  both directions; polls while the thread stays open. */
export function useThreadPresence(threadId: number | null) {
  return useQuery<{ viewers: PresenceViewer[] }>({
    queryKey: ['thread-presence', threadId],
    enabled: threadId != null,
    refetchInterval: 15_000,
    queryFn: async () => {
      const r = await fetch(`/api/threads/${threadId}/presence`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) return { viewers: [] }; // best-effort — never break the pane
      return r.json();
    },
  });
}

/** 409 payload from the reply collision guard. */
export interface ThreadChangedInfo {
  newMessage: { from: string; at: string } | null;
  teammateReply: { by: string; at: string | null } | null;
}

export function useThreadDraft(threadId: number | null) {
  return useQuery<ThreadDraft>({
    queryKey: ['thread-draft', threadId],
    enabled: threadId != null,
    queryFn: async () => {
      const r = await fetch(`/api/threads/${threadId}/draft`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    // Poll so peer-drafting shows up reasonably quickly.
    refetchInterval: 20_000,
  });
}

export function useSaveDraft(threadId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { body: string; attachmentIds?: number[] }) => {
      const r = await fetch(`/api/threads/${threadId}/draft`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    // Keep the cached draft in step with what we just saved. Without this the
    // cache lags the server by up to a full poll interval (20s), and every
    // consumer that keys off draft.body (the auto-open-reply effect!) acts on
    // stale text — the root cause of the phantom-composer / duplicate-send bug.
    onSuccess: (_data, input) => {
      qc.setQueryData<ThreadDraft>(['thread-draft', threadId], (old) => ({
        body: input.body,
        attachments: old?.attachments ?? [],
        updatedAt: new Date().toISOString(),
        peers: old?.peers ?? [],
      }));
    },
  });
}

export function useClearDraft(threadId: number) {
  return useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/threads/${threadId}/draft`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
  });
}

// --- Mentions / notifications --------------------------------------------

export interface Mention {
  id: number;
  threadId: number;
  body: string;
  authorName: string;
  authorEmail: string;
  createdAt: string;
}

export function useMentions() {
  return useQuery<{ mentions: Mention[]; lastSeenId: number }>({
    queryKey: ['my-mentions'],
    queryFn: async () => {
      const r = await fetch('/api/me/mentions', { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useMarkMentionsSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (lastSeenId: number) => {
      const r = await fetch('/api/me/mentions/seen', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ lastSeenId }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-mentions'] }),
  });
}

// --- Tags -----------------------------------------------------------------

export interface Tag {
  id: number;
  name: string;
  color: string;
}

export function useTags() {
  return useQuery<{ tags: Tag[] }>({
    queryKey: ['tags'],
    queryFn: async () => {
      const r = await fetch('/api/tags', { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    staleTime: 60_000,
  });
}

export function useCreateTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, color }: { name: string; color: string }) => {
      const r = await fetch('/api/tags', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name, color }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      return r.json() as Promise<{ ok: true; tag: Tag }>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tags'] }),
  });
}

export function useDeleteTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/tags/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tags'] });
      void qc.invalidateQueries({ queryKey: ['threads'] });
      void qc.invalidateQueries({ queryKey: ['thread'] });
    },
  });
}

export function useApplyTag(threadId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tagId: number) => {
      const r = await fetch(`/api/threads/${threadId}/tags`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ tagId }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
      void qc.invalidateQueries({ queryKey: ['thread', threadId] });
    },
  });
}

export function useRemoveTag(threadId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tagId: number) => {
      const r = await fetch(`/api/threads/${threadId}/tags/${tagId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
      void qc.invalidateQueries({ queryKey: ['thread', threadId] });
    },
  });
}

// --- Compose new thread ---------------------------------------------------

export function useComposeThread() {
  return useMutation<
    SendQueuedResponse,
    Error,
    {
      accountId: number;
      to: string[];
      cc?: string[];
      bcc?: string[];
      subject?: string;
      body: string;
      bodyHtml?: string;
      scheduledAt?: string;
      attachmentIds?: number[];
      signatureId?: number | null;
    }
  >({
    mutationFn: async (args) => {
      const payload = args.bodyHtml
        ? { ...args, bodyHtml: autolinkHtml(args.bodyHtml) }
        : args;
      const r = await fetch('/api/compose', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
  });
}

// --- Assignments ----------------------------------------------------------

export function useAssign(threadId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (assigneeUserId: number | null) => {
      const r = await fetch(`/api/threads/${threadId}/assign`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ assigneeUserId }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['thread', threadId] });
      void qc.invalidateQueries({ queryKey: ['threads'] });
    },
  });
}
